import './client-users.scss'

import { useMachine } from '@xstate/react'
import { useEffect } from 'react'

import { UserInformationBasicDfm, UserStatus } from '../../../api/_legacy/generated-api'
import { useClientsLayout } from '../clients-layout-provider'
import { Counts, OperationName } from '../common/users.state'
import { User } from '../types'
import { UsersList } from '../users-list/users-list'
import { clientUsersMachine, dfmUsersMachine } from './state'

type ClientUsersStateless = {
  clientId: string
  counts: Counts
  filterStatus?: UserStatus
  notifyParentOfSave: () => void
  onActivate: () => void
  onDelete: () => void
  onEdit: (userName: string) => void
  onFilterByStatus: (status: 'All' | UserStatus) => void
  onSearch: (searchTerm: string) => void
  onUnlock: () => void
  operation: OperationName
  selectedUser?: User | UserInformationBasicDfm
  users: (User | UserInformationBasicDfm)[]
}

export function ClientUsersStateless({
  clientId,
  counts,
  filterStatus,
  notifyParentOfSave,
  onActivate,
  onDelete,
  onEdit,
  onFilterByStatus,
  onSearch,
  onUnlock,
  operation,
  selectedUser,
  users
}: ClientUsersStateless) {
  const { isPortfolioManagerScreen } = useClientsLayout()

  return (
    <UsersList
      addNewLinkText={`Add a new ${isPortfolioManagerScreen ? 'portfolio manager user' : 'user'}`}
      clientId={clientId}
      counts={counts}
      filterStatus={filterStatus}
      notifyParentOfSave={notifyParentOfSave}
      operation={operation}
      selectedUser={selectedUser}
      userType="client"
      users={users}
      onActivate={onActivate}
      onDelete={onDelete}
      onEdit={onEdit}
      onFilterByStatus={onFilterByStatus}
      onSearch={onSearch}
      onUnlock={onUnlock}
    />
  )
}

export type Props = {
  clientId: string
}

export function ClientUsers({ clientId }: Props) {
  const { isPortfolioManagerScreen } = useClientsLayout()

  const [machine, send] = useMachine(
    isPortfolioManagerScreen ? dfmUsersMachine : clientUsersMachine
  )

  useEffect(() => {
    send({ type: 'FETCH_USERS', clientId })
  }, [clientId, send])

  const { context } = machine

  return (
    <ClientUsersStateless
      clientId={clientId}
      counts={context.counts}
      filterStatus={context.filterStatus}
      notifyParentOfSave={() => send({ type: 'USER_SAVED' })}
      operation={context.operation}
      selectedUser={context.selectedUser}
      users={context.filteredUsers}
      onActivate={() => {
        send({ type: 'ACTIVATE' })
      }}
      onDelete={() => {
        send({ type: 'DELETE' })
      }}
      onEdit={userName => send({ type: 'SELECT_USER', userName })}
      onFilterByStatus={(status: UserStatus) => send({ type: 'FILTER_BY_STATUS', status })}
      onSearch={searchTerm => send({ type: 'SEARCH_USERS', searchTerm })}
      onUnlock={() => {
        send({ type: 'UNLOCK' })
      }}
    />
  )
}
